import { h } from 'preact';
import { useEffect, useMemo } from 'preact/hooks';
import { useRecsProvider } from '../search/useSearchProvider';
import { ProductCard } from '../components/product/ProductCard';
import { SkeletonCard } from '../components/product/SkeletonCard';
import { Slider } from '../components/slider';
import { useColourwayStorefrontData } from '../utils/useColourwayStorefrontData';
import { readBoostRecsOptionsFromDataset } from '../platforms/boost/api';
import { detectPageType } from '../platforms/boost/analytics/utils';
import { TrackingProvider } from '../analytics/context';
import styles from './ProductRecommendationSection.module.scss';
export function ProductRecommendationSection({ providerType = 'boost', mockPlatform, seedProductId = '', recsType = 'related-items', title, skeletonCount = 6, recsOptions, mountElement, }) {
    var _a, _b;
    const awaitRefresh = (_a = mountElement === null || mountElement === void 0 ? void 0 : mountElement.hasAttribute('await-refresh')) !== null && _a !== void 0 ? _a : false;
    // Dataset is the default source for payload options; `recsOptions` (for tests
    // and programmatic callers) wins on a per-field basis via spread order.
    const options = Object.assign(Object.assign({}, readBoostRecsOptionsFromDataset(mountElement)), recsOptions);
    const { loading, products, meta, loadRecs } = useRecsProvider(providerType, mockPlatform, Object.assign(Object.assign({ seedProductId,
        recsType }, options), { awaitRefresh }));
    // Every card in this carousel is a Boost "recommend"-surface impression, so
    // mount a TrackingProvider: ProductCard's click / add-to-cart handlers fire
    // with itemListId 'recommendations' and the recs response's rid
    // (correlationId). Without it the card's `if (!tracking) return` guard
    // silently drops the event — which is why recs clicks weren't tracked.
    const trackingValue = useMemo(() => {
        var _a, _b;
        return ({
            itemListId: 'recommendations',
            pageType: (_a = options.pageType) !== null && _a !== void 0 ? _a : detectPageType(),
            correlationId: (_b = meta === null || meta === void 0 ? void 0 : meta.analytics) === null || _b === void 0 ? void 0 : _b.queryID,
            widgetId: options.widgetId,
        });
    }, [options.pageType, options.widgetId, (_b = meta === null || meta === void 0 ? void 0 : meta.analytics) === null || _b === void 0 ? void 0 : _b.queryID]);
    // Lazy-load related-colourway Storefront data for visible recs cards so swatch
    // clicks swap the image/title/sizes the same way they do in the main product grid.
    const { colourwayData, observeCard } = useColourwayStorefrontData(products, {
        enabled: !loading,
    });
    // Wire up the mount's refresh lifecycle.
    //
    //   - `__refreshRecs` is attached to the DOM element as the internal entry
    //     point used by `window.mrRecommendations.refresh(selector)`. It's an
    //     implementation detail (double-underscore prefix signals "private");
    //     external code should always go through the `refresh()` helper or the
    //     `mr:recs:ready` event — see docs/boost/recommendations.md.
    //   - `mr:recs:ready` (bubbling) is dispatched once `__refreshRecs` is wired,
    //     so the helper's queueing can resolve and advanced consumers can run
    //     arbitrary logic when a mount is ready.
    //   - `mr:recs:unready` (bubbling) on cleanup mirrors the pair.
    useEffect(() => {
        if (!mountElement)
            return;
        mountElement.__refreshRecs = loadRecs;
        mountElement.dispatchEvent(new CustomEvent('mr:recs:ready', { bubbles: true }));
        return () => {
            delete mountElement.__refreshRecs;
            mountElement.dispatchEvent(new CustomEvent('mr:recs:unready', { bubbles: true }));
        };
    }, [mountElement, loadRecs]);
    if (!loading && products.length === 0) {
        return null;
    }
    return (h(TrackingProvider, { value: trackingValue },
        h("section", { class: styles.recsSection },
            title && h("h2", { class: styles.title }, title),
            h(Slider, null, loading
                ? Array.from({ length: skeletonCount }, (_, i) => h(SkeletonCard, { key: `skeleton-${i}` }))
                : products.map(product => {
                    const pid = String(product.id);
                    return (h("div", { key: product.id, ref: el => observeCard(pid, el), "data-product-id": pid },
                        h(ProductCard, { product: product, layout: "gridFour", colourwayStorefrontData: colourwayData })));
                })))));
}
